const READY_STATE_LABELS = Object.freeze([
  "HAVE_NOTHING", "HAVE_METADATA", "HAVE_CURRENT_DATA", "HAVE_FUTURE_DATA", "HAVE_ENOUGH_DATA",
]);
const NETWORK_STATE_LABELS = Object.freeze(["EMPTY", "IDLE", "LOADING", "NO_SOURCE"]);
const CONTEXT_STATES = new Set(["running", "suspended", "closed"]);
const FORMAT_BY_EXTENSION = Object.freeze({
  ".aac": "audio/aac", ".flac": "audio/flac", ".m4a": "audio/mp4", ".mp3": "audio/mpeg",
  ".ogg": "audio/ogg", ".opus": "audio/ogg; codecs=opus", ".wav": "audio/wav", ".webm": "audio/webm",
});

function finite(value, minimum, maximum) {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(minimum, Math.min(maximum, number)) : null;
}

function text(value, maximum = 96) {
  return value === null || value === undefined ? "" : String(value).trim().slice(0, maximum);
}

function stateLabel(value, labels) {
  const index = Number(value);
  return Number.isInteger(index) && labels[index] ? labels[index] : "UNKNOWN";
}

function extensionFormat(value) {
  const filename = text(value, 512).split(/[?#]/, 1)[0].toLowerCase();
  const extension = filename.slice(filename.lastIndexOf("."));
  return FORMAT_BY_EXTENSION[extension] || "unknown";
}

function authoredSourceFormat(track = {}, media = null) {
  const explicit = text(track?.audio_type || track?.mime_type || track?.audio_mime_type, 96);
  return explicit.includes("/")
    ? explicit
    : extensionFormat(track?.file || track?.audio_file || media?.currentSrc);
}

/**
 * Describe only the observable browser audio graph and media transport.
 * Output topology is a graph capability, not proof of physical speakers or
 * headphones; source format/codec values are authored hints, not decoder
 * measurements. No user-agent, microphone, URL, or raw audio is returned.
 */
export function resolveAudioTransportProfile({ context = null, media = null, track = {} } = {}) {
  const destination = context?.destination;
  const channelCount = finite(destination?.channelCount, 0, 64);
  const maxChannelCount = finite(destination?.maxChannelCount, 0, 64);
  const profile = {
    transport_profile_version: "audio-transport.v1",
    audio_context_state: CONTEXT_STATES.has(String(context?.state)) ? String(context.state) : "unknown",
    audio_destination_channel_count: channelCount,
    audio_destination_max_channel_count: maxChannelCount,
    audio_channel_mode: channelCount === 1 || maxChannelCount === 1
      ? "mono"
      : channelCount >= 2 || maxChannelCount >= 2
        ? "stereo_capable"
        : "unknown",
    audio_media_ready_state: stateLabel(media?.readyState, READY_STATE_LABELS),
    audio_media_network_state: stateLabel(media?.networkState, NETWORK_STATE_LABELS),
    audio_source_format_hint: authoredSourceFormat(track, media),
  };
  const sampleRate = finite(context?.sampleRate, 8_000, 192_000);
  const baseLatency = finite(Number(context?.baseLatency) * 1000, 0, 60_000);
  const outputLatency = finite(Number(context?.outputLatency) * 1000, 0, 60_000);
  const duration = finite(media?.duration, 0, 86_400);
  if (sampleRate !== null) profile.audio_context_sample_rate = Math.round(sampleRate);
  if (baseLatency !== null) profile.audio_base_latency_ms = Number(baseLatency.toFixed(3));
  if (outputLatency !== null) profile.audio_output_latency_ms = Number(outputLatency.toFixed(3));
  if (duration !== null) profile.audio_media_duration_seconds = Number(duration.toFixed(3));
  const codecHint = text(track?.audio_codec || track?.codec || track?.audio_codec_hint, 96);
  if (codecHint) profile.audio_source_codec_hint = codecHint;
  return profile;
}

export { READY_STATE_LABELS, NETWORK_STATE_LABELS };
